import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { BreadcrumbsItem } from "../components/Breadcrumbs";
import { translations } from "../lang/Language";
import { SupportedLanguage } from "../constants";
import { Clinic, navigationItems } from "../types";
import { isRtlLanguage } from "../utils";

type AppContextType = {
  user: any,
  setUser: (user: any) => void;
  clinics: Clinic[];
  setClinics: (clinics: Clinic[]) => void;
  addBreadcrumb: (breadcrumb: BreadcrumbsItem) => void;
  updateBreadcrumbs: (breadcrumbs: BreadcrumbsItem[]) => void;
  breadcrumbs: readonly BreadcrumbsItem[];
  actions: ReactElement[];
  updateActions: (actions: ReactElement[]) => void;
  translate: (
    category: string,
    key?: string,
    params?: Record<string, string>
  ) => any;
  setLanguage: (language: SupportedLanguage) => void;
  getTranslations: () => any;
  currentLanguage: SupportedLanguage;
  currentTool: string;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface UserConfig {
  weightHeightUnit?: "metric" | "imperial";
  language?: string;
  hormoneLevelUnit?: "pg/ml" | "pmol/l";
}

interface User {
  clinic: string;
  config: UserConfig;
  email?: string;
  name?: string;
  signInDetails?: any;
  userId: string;
  username: string;
  status: 0 | 1;
  role: number;
  clinicName: string;
}

const splitPath = (path: string): string[] => {
  const regex = /([^.[\]]+)|\["([^"]+)"\]|\['([^']+)'\]/g;
  const matches = path.matchAll(regex);
  return Array.from(matches).map((match) => match[1] ?? match[2] ?? match[3]);
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbsItem[]>([]);
  const [actions, setActions] = useState<ReactElement[]>([]);
  const [currentTool, setCurrentTool] = useState<string>("");
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>("en");
  const [user, setUser] = useState<User | null>();
  const [clinics, setClinics] = useState<Clinic[]>([]);

  useEffect(() => {
    const language = (localStorage.getItem("language") ?? user?.config?.language) as SupportedLanguage;
    if (language && language !== currentLanguage) {
      setLanguage(language);
    }
  }, [user]);

  const addBreadcrumb = (breadcrumb: BreadcrumbsItem) => {
    setBreadcrumbs([...breadcrumbs, breadcrumb]);
  };

  const updateBreadcrumbs = (breadcrumbs: BreadcrumbsItem[]) => {
    setBreadcrumbs(breadcrumbs);
  };

  const updateActions = (actions: ReactElement[]) => {
    setActions(actions);
  };

  const setLanguage = (language: SupportedLanguage) => {
    console.log("setting language", language);
    setCurrentLanguage(language);
    localStorage.setItem("language", language);
  };

  useEffect(() => {
    if (isRtlLanguage(currentLanguage)) {
      document.body.setAttribute("dir", "rtl");

    }
  }, [currentLanguage]);

  const translate = (
    category: string,
    path?: string,
    params?: Record<string, any>
  ) => {
    try {
      let ret = translations[currentLanguage][category];
      if (path) {
        splitPath(path).forEach((k) => {
          ret = ret[k];
        });
      }
      if (params && typeof ret === "string") {
        ret = ret.replace(/\{(\w+)\}/g, (_m, key) => {
          return params[key] ?? _m;
        });
      }
      return ret;
    } catch (error) {
      console.error(`Translation error for ${category}.${path}:`, error);
      return path;
    }
  };

  const getTranslations = () => {
    return translations[currentLanguage];
  };

  useEffect(() => {
    const paths = window.location.pathname.split("/");
    if (
      paths.length > 1 &&
      navigationItems.find((item) => item.path === paths[1])
    ) {
      setCurrentTool(paths[1]);
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        clinics,
        setClinics,
        breadcrumbs,
        addBreadcrumb,
        updateBreadcrumbs,
        actions,
        updateActions,
        translate,
        setLanguage,
        getTranslations,
        currentLanguage,
        currentTool,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
